import { useRef, useState } from "react";
import { FieldInput } from "./FieldInput";
import { CONTAMINANTS } from "../../lib/defaults";
import { parseWaterAnalysis } from "../../lib/api";
import { Upload, Loader2, FileCheck, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Step 1: Client info + Water Quality (with optional AI lab-report upload)
 * Replaces previous separate "Project" and "Water Analysis" steps.
 */
export default function StepClient({ state, set, setMany }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);

  async function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setParsed(null);
    try {
      const data = await parseWaterAnalysis(f);
      // Map parsed fields → wizard state (only non-null values)
      const patch = {};
      const NUMERIC = ["iron_ppm","manganese_ppm","h2s_ppm","ammonia_ppm","tannins_ppm","arsenic_ppm","nitrites_ppm","toc_ppm","turbidity_ntu","ph","temp_f"];
      const BOOL = ["iron_bacteria_present","coliform_present"];
      NUMERIC.forEach(k => {
        if (data[k] !== null && data[k] !== undefined) patch[k] = data[k];
      });
      BOOL.forEach(k => {
        if (data[k] !== null && data[k] !== undefined) patch[k] = !!data[k];
      });
      // If TOC was returned, auto-enable the TOC checkbox
      if (data.toc_ppm !== null && data.toc_ppm !== undefined) patch.toc_enabled = true;
      setMany(patch);
      setParsed(data);
      toast.success("Lab report parsed and form pre-filled");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not parse the file");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <section data-testid="step-client" className="space-y-8">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
          Client &<br/>Water Quality
        </h1>
        <p className="text-white/70 text-base mt-4 max-w-xl">
          Enter the customer's name and address, then upload a lab report or enter the water-analysis values manually.
        </p>
      </div>

      {/* Client info */}
      <div className="space-y-4">
        <FieldInput id="project_name" testId="input-project-name" label="Client Name"
          type="text" value={state.project_name} onChange={v => set("project_name", v)}
          placeholder="Smith Residence" />
        <FieldInput id="site_address" testId="input-site-address" label="Site Address"
          type="text" value={state.site_address} onChange={v => set("site_address", v)}
          placeholder="123 Glacier Rd. — Wasilla AK" />
      </div>

      {/* Upload water-analysis */}
      <div className="bg-[#111111] border-2 border-[#F26522] p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F26522] flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-lg font-black uppercase text-white">Upload Lab Report</div>
            <div className="font-mono text-[10px] tracking-wider uppercase text-[#F26522]">
              AI-Powered · PDF / JPG / PNG
            </div>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/jpeg,image/jpg,image/png,image/webp"
          onChange={onFile}
          data-testid="input-lab-upload"
          className="hidden"
        />
        <button
          type="button"
          data-testid="btn-upload-lab"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="w-full h-16 border-2 border-dashed border-white/30 hover:border-[#F26522] bg-black text-white font-heading font-black tracking-wider uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        >
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Parsing report…</>
                : <><Upload className="w-5 h-5" /> Choose PDF / Image</>}
        </button>

        {parsed && (
          <div className="bg-black border-l-4 border-[#10B981] p-3 text-sm text-white/90" data-testid="parsed-summary">
            <div className="flex items-center gap-2 mb-1">
              <FileCheck className="w-4 h-4 text-[#10B981]" />
              <div className="font-mono text-[10px] uppercase tracking-wider text-[#10B981]">Pre-filled from {parsed.lab_name || "lab report"}</div>
            </div>
            {parsed.notes && <p className="text-xs text-white/70 mt-2">{parsed.notes}</p>}
          </div>
        )}

        <p className="text-xs text-white/50">
          Or skip the upload and enter values manually below — empty = not tested, zero = tested & not present.
        </p>
      </div>

      {/* Manual water analysis */}
      <div className="space-y-5">
        <div>
          <div className="font-heading text-xl font-black uppercase text-white mb-1">Water Analysis</div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#F26522]">Manual Entry</div>
        </div>

        {/* Water analytes — 2-column on wide screens for less scroll */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5">
          {CONTAMINANTS.map(c => (
            <FieldInput key={c.key} id={c.key}
              testId={`input-${c.key.replace(/_/g, "-")}`}
              label={c.label} unit={c.unit}
              value={state[c.key]} onChange={v => set(c.key, v)}
              placeholder="0.0" step="0.01" min="0" />
          ))}

          {/* TOC checkbox + conditional input — spans both columns */}
          <div className="bg-[#111111] border-2 border-white/15 p-4 lg:col-span-2">
          <button
            type="button"
            data-testid="toggle-toc"
            onClick={() => set("toc_enabled", !state.toc_enabled)}
            className="w-full text-left flex items-center justify-between gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-base text-white uppercase">Total Organic Carbon (TOC)</div>
              <div className="text-xs text-white/60 mt-1">Check only if you have a TOC reading. Auto-checked when a lab report is uploaded.</div>
            </div>
            <div className={`w-6 h-6 border-2 shrink-0 flex items-center justify-center ${state.toc_enabled ? "bg-[#F26522] border-[#F26522]" : "bg-transparent border-white/30"}`}>
              {state.toc_enabled && <div className="w-2.5 h-2.5 bg-black" />}
            </div>
          </button>
          {state.toc_enabled && (
            <div className="mt-4">
              <FieldInput id="toc_ppm" testId="input-toc-ppm"
                label="TOC Reading" unit="ppm"
                value={state.toc_ppm} onChange={v => set("toc_ppm", v)}
                placeholder="0.0" step="0.01" min="0" />
            </div>
          )}
        </div>

        {/* pH + Temperature (master variables) */}
        <FieldInput id="ph" testId="input-ph" label="pH"
          value={state.ph} onChange={v => set("ph", v)}
          placeholder="7.5" step="0.1" min="0" max="14" />
        <FieldInput id="temp_f" testId="input-temp-f"
          label="Water Temperature" unit="°F"
          value={state.temp_f} onChange={v => set("temp_f", v)}
          placeholder="55" step="1" />
        </div>

        {/* H2S mode */}
        <div className="bg-[#111111] border-2 border-white/15 p-5">
          <div className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/70 font-bold mb-4">
            Hydrogen Sulfide Treatment Goal
          </div>
          <div className="space-y-3">
            {[
              { v: "odor", label: "Odor Removal", desc: "Precipitate to elemental sulfur. Standard for taste/smell complaints." },
              { v: "complete", label: "Complete Oxidation", desc: "Full conversion to dissolved sulfate. Use for high H₂S sites." },
            ].map(opt => (
              <button key={opt.v} type="button"
                data-testid={`h2s-mode-${opt.v}`}
                onClick={() => set("h2s_mode", opt.v)}
                className={`w-full text-left p-4 border-2 transition-colors ${
                  state.h2s_mode === opt.v
                    ? "border-[#F26522] bg-black"
                    : "border-white/15 bg-[#1A1A1A] hover:border-white/40"
                }`}>
                <div className="font-heading font-bold text-base text-white uppercase">{opt.label}</div>
                <div className="text-xs text-white/60 mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Biological flags */}
        <div className="space-y-3">
          {[
            { k: "iron_bacteria_present", testId: "toggle-iron-bacteria", label: "Iron / Sulfur Bacteria", desc: "Slimy biofilm. Triggers post-install plumbing-flush protocol." },
            { k: "coliform_present", testId: "toggle-coliform", label: "Coliform / E.coli Detected", desc: "Surface-water pathway. Disinfection required." },
          ].map(b => (
            <button key={b.k} type="button" data-testid={b.testId}
              onClick={() => set(b.k, !state[b.k])}
              className={`w-full text-left p-4 border-2 transition-colors flex items-start justify-between gap-3 ${
                state[b.k] ? "border-[#F26522] bg-black" : "border-white/15 bg-[#1A1A1A] hover:border-white/40"
              }`}>
              <div>
                <div className="font-heading font-bold text-base text-white uppercase">{b.label}</div>
                <div className="text-xs text-white/60 mt-1">{b.desc}</div>
              </div>
              <div className={`w-7 h-7 border-2 shrink-0 flex items-center justify-center ${state[b.k] ? "bg-[#F26522] border-[#F26522]" : "bg-transparent border-white/30"}`}>
                {state[b.k] && <div className="w-2.5 h-2.5 bg-black" />}
              </div>
            </button>
          ))}
        </div>

        {(state.iron_bacteria_present || state.coliform_present) && (
          <div className="bg-[#F26522] text-black p-5 border-2 border-[#F26522]" data-testid="bio-warning">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 shrink-0 mt-0.5" />
              <div className="font-heading font-black uppercase tracking-wide text-base">Biofilm Flush Protocol</div>
            </div>
            <ol className="space-y-2 text-sm list-decimal list-inside font-medium">
              <li>Verify the <strong>Chlorine Titan is IN BYPASS</strong> after the injection system is installed and powered.</li>
              <li>Run water at every faucet (hot &amp; cold) and every fixture in the home until you smell strong chlorine, and verify chlorine residual at the tap with chlorine test strips.</li>
              <li>Take the <strong>Chlorine Titan OUT of bypass</strong>.</li>
              <li>Run every faucet again until the chlorine smell clears and test strips show <strong>NO residual at the tap</strong>. The Chlorine Titan is now in service.</li>
            </ol>
          </div>
        )}
      </div>
    </section>
  );
}
